import * as React from "react";
import { useDispatch, useSelector } from "react-redux";

import { useCustomSearchParams } from "../../../hooks/useCustomSearchParams";
import { setDateRange } from "../../../redux/SearchSlice";
import { useResponsive } from "../../../hooks/useResponsive";
import {
  createMyTaskStaticRanges,
  getDateRangeHeading,
  getMatchingStaticRangeLabel,
  parseWorklogDateRange,
} from "../utils/myTaskUtils";

function isSameDateRange(currentRange, nextRange) {
  if (!currentRange && !nextRange) {
    return true;
  }

  if (!currentRange || !nextRange) {
    return false;
  }

  return (
    new Date(currentRange.startDate).getTime() ===
      new Date(nextRange.startDate).getTime() &&
    new Date(currentRange.endDate).getTime() ===
      new Date(nextRange.endDate).getTime()
  );
}

export function useSelectDatePickerState() {
  const dispatch = useDispatch();
  const [oldDate] = useCustomSearchParams("date");
  const dateRange = useSelector((state) => state.search.dateRange);
  const isMobile = useResponsive("down", "md");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedDateLabel, setSelectedDateLabel] = React.useState("");
  const parsedDateRange = React.useMemo(
    () => parseWorklogDateRange(oldDate),
    [oldDate]
  );
  const open = Boolean(anchorEl);

  const customStaticRanges = React.useMemo(() => createMyTaskStaticRanges(), []);
  const heading = React.useMemo(() => getDateRangeHeading(dateRange), [dateRange]);

  React.useEffect(() => {
    if (
      parsedDateRange &&
      !isSameDateRange(dateRange, parsedDateRange)
    ) {
      dispatch(setDateRange(parsedDateRange));
    }
  }, [dateRange, dispatch, parsedDateRange]);

  React.useEffect(() => {
    if (isMobile) {
      return undefined;
    }

    return () => {
      dispatch(setDateRange(null));
    };
  }, [dispatch, isMobile]);

  React.useEffect(() => {
    setSelectedDateLabel(
      getMatchingStaticRangeLabel(parsedDateRange, customStaticRanges)
    );
  }, [customStaticRanges, parsedDateRange]);

  const handleSelectedLabel = React.useCallback((value) => {
    setSelectedDateLabel(value ?? "");
  }, []);

  const handleOpen = React.useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = React.useCallback(() => {
    setAnchorEl(null);
  }, []);

  return {
    open,
    anchorEl,
    heading,
    selectedDateLabel,
    customStaticRanges,
    handleSelectedLabel,
    handleOpen,
    handleClose,
  };
}
